import { ChangeDetectorRef, Component, OnInit, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CountryRulesService } from '@agenda-customer-ui/data-access';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { IOtherPhone } from '@core/interface';

@Component({
  selector: 'agenda-customer-ui-form-phone',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgxMaskDirective],
  templateUrl: './phone.component.html',
  styleUrls: ['./phone.component.scss'],
  providers: [provideNgxMask()]
})
export class FormPhoneComponent implements OnInit {
  // MASCARAS
  countryRules = input<CountryRulesService>();
  maskCityCode = '';
  maskPhone = '';

  error = 'update-delivery-address.form.mandatory-message';

  // TEXTS
  label = input<string>('');
  legend = input<string>('');

  // VALORES INICIAIS
  initialValuesInputs = input<IOtherPhone>();

  // INPUTS
  isDisabledCityCode = false;
  origin = input<string>('SCHEDULE');
  emitFormValid = output<boolean>();
  newPhone = output<IOtherPhone>();
  phoneForm!: FormGroup;
  isValidFields!: boolean;

  constructor(
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => this.handleDisableEnableFields());
    effect(() => {
      const service = this.countryRules();
      if (service?.active()) {
        const masks = service.masksSignal();
        if (masks?.maskCityCode && masks?.maskPhone) {
          this.maskCityCode = masks.maskCityCode;
          this.maskPhone = masks.maskPhone;
        }
      }
    });
  }

  handleDisableEnableFields = () => {
    if (this.countryRules()?.disabledAllFieldsPhone()) {
      this.phoneForm.disable();
    } else {
      if (!this.isDisabledCityCode) {
        this.phoneForm.get('cityCode')?.enable();
      }
      this.phoneForm.get('phoneNumber')?.enable();
    }
  }

  ngOnInit() {
    if (this.countryRules()?.active()) {
      this.maskCityCode = this.countryRules()?.masksSignal()?.maskCityCode ?? '';
      this.maskPhone = this.countryRules()?.masksSignal()?.maskPhone ?? '';
    }

    this.isDisabledCityCode = this.countryRules()?.isDisabledCityCode() ?? false;
    const initialValueCityCode = this.handleValueCityCode(this.initialValuesInputs()?.otherPhone?.cityCode ?? '');

    this.phoneForm = new FormGroup({
      cityCode: this.createFormControl(this.maskCityCode, initialValueCityCode, this.isDisabledCityCode),
      phoneNumber: this.createFormControl(this.maskPhone, this.initialValuesInputs()?.otherPhone?.phoneNumber ?? '')
    });

    this.emitFormState();

    if (this.origin() === 'SHELL') {
      setTimeout(() => {
        this.isValidFields = (this.hasErrorFieldsForShell() ?? false);
        this.cdr.detectChanges();
      }, 0);
    }
  }

  private createFormControl(masks: string, value: string, disabled = false): FormControl {
    const lengths = this.calculateLengths(masks);
    
    const validators = [Validators.required, Validators.pattern('^[0-9]*$')];
    
    if (masks && masks.length > 0) {
      validators.push(Validators.minLength(lengths.min));
      validators.push(Validators.maxLength(lengths.max));
    }
    
    return new FormControl({ value, disabled }, validators);
  }

  private calculateLengths(mask: string): { min: number; max: number } {
    const masksDivided = this.splitMasks(mask, "||");
    return this.findLengths(masksDivided);
  }

  private splitMasks = (str: string, delimiter: string): string[] => {
    if (!delimiter) {
      throw new Error("Invalid delimiter.");
    }

    return str.split(delimiter);
  }

  private findLengths = (masksDivided: string[]): { min: number; max: number } => {
    let min = masksDivided[0].length;
    let max = masksDivided[0].length;

    for (let i = 1; i < masksDivided.length; i++) {
      min = Math.min(min, masksDivided[i].length);
      max = Math.max(max, masksDivided[i].length);
    }

    return { min, max };
  }

  private handleValueCityCode = (value: string) => this.isDisabledCityCode ? '9' : value;

  private emitFormState = () => {
    this.phoneForm.valueChanges.subscribe(() => {
      this.isValidFields = this.origin() === 'SHELL' ? this.hasErrorFieldsForShell() : this.hasErrorFields();

      const otherPhone = {
        cityCode: this.handleValueCityCode(this.phoneForm.get('cityCode')?.value ?? ''),
        phoneNumber: this.phoneForm.get('phoneNumber')?.value ?? ''
      };

      this.newPhone.emit({ otherPhone });
      this.emitFormValid.emit(this.phoneForm.valid);
    });
  }

  private hasErrorFieldsForShell() {
    this.error = 'update-delivery-address.form.mandatory-message';

    const cityCodeControl = this.phoneForm.get('cityCode');
    const phoneNumberControl = this.phoneForm.get('phoneNumber');

    if (phoneNumberControl?.errors?.['minlength'] || cityCodeControl?.errors?.['minlength'] || 
        cityCodeControl?.errors?.['maxlength'] || phoneNumberControl?.errors?.['maxlength']) {
      this.error = "update-phone-error-invalid-phone";
      return true;
    }

    return cityCodeControl?.invalid || phoneNumberControl?.invalid;
  }

  private hasErrorFields() {
    this.error = 'update-delivery-address.form.mandatory-message';

    if (this.phoneForm.get('phoneNumber')?.errors?.['minlength'] || this.phoneForm.get('cityCode')?.errors?.['minlength'] || this.phoneForm.get('cityCode')?.errors?.['maxlength'] || this.phoneForm.get('phoneNumber')?.errors?.['maxlength']) {
      this.error = "update-phone-error-invalid-phone";
      return true;
    }

    return this.phoneForm.get('cityCode')?.errors?.['required'] || this.phoneForm.get('phoneNumber')?.errors?.['required'] || this.phoneForm.get('cityCode')?.errors?.['pattern'] || this.phoneForm.get('phoneNumber')?.errors?.['pattern'] || !this.phoneForm.get('cityCode')?.value || !this.phoneForm.get('phoneNumber')?.value;
  }

}
